import { useCallback, useEffect, useRef, useState } from "react";
import { onAskQuestion } from "../lib/askEvents";
import type { League, PipelineStatus } from "../types";
import { useChatStore } from "../store/chatStore";
import { useSessionStore } from "../store/sessionStore";
import { useMaintenanceStore } from "../store/maintenanceStore";
import { useAsk } from "../hooks/useAsk";
import { useVoiceInput } from "../hooks/useVoiceInput";
import { fetchVoiceConfig } from "../api";
import { setAudioSession } from "../lib/audioSession";
import { resolveEngine, sttOverride, type SttEngine } from "../lib/serverStt";
import { useAudioPlayer } from "../hooks/useAudioPlayer";
import Header from "../components/Header";
import ChatThread from "../components/ChatThread";
import InputBar from "../components/InputBar";
import StatusIndicator from "../components/StatusIndicator";
import Tour from "../components/Tour";

// Sentence-boundary regex: end punctuation followed by whitespace.
// We intentionally do NOT match end-of-buffer (`$`) because the LLM tokenizer
// often splits decimals like "6.5" into tokens "6", ".", "5" — mid-stream the
// buffer briefly ends in "...6." and an end-of-buffer match would flush
// prematurely, causing TTS to read "6.5 DPT" as "six [pause] five DPT".
// The final sentence (no trailing whitespace) is flushed by onDone's
// remaining-buffer handler.
const SENTENCE_END = /[.!?]\s/;

// What the mic line says when a server-mode clip could not be used (the
// recording is gone, so the user has to say it again).
const NOTICE_FALLBACK = "Scout's ears are busy — tap the mic and say that again.";
const NOTICE_MISS = "Scout didn't catch that — tap the mic and try again.";
const NOTICE_MS = 4_000;

function tabStorage(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null; // private mode may throw on access
  }
}

export default function Chat() {
  const [league, setLeague] = useState<League | null>(null);
  const [inputValue, setInputValue] = useState("");

  const { addUserMessage, addAssistantMessage, appendToken, setVisual, setStreamingDone, setError, updateMessageContent } =
    useChatStore();
  const messages = useChatStore((s) => s.messages);

  const { sessionId, initSession } = useSessionStore();
  useEffect(() => { void initSession(); }, [initSession]);

  const { ask, isAsking, status, setStatus } = useAsk();
  const isMaintenance = useMaintenanceStore((s) => s.isMaintenance);
  const setMaintenance = useMaintenanceStore((s) => s.setMaintenance);

  // VITE_VOICE_BACKEND=true  → server STT (STT_PROVIDER=whisper locally) + Kokoro TTS
  // VITE_VOICE_BACKEND=false → browser speechSynthesis; STT per /voice/config below
  const voiceBackend = import.meta.env.VITE_VOICE_BACKEND === "true";

  // Which speech engine this tab records with (decisions/0015): the backend's
  // /voice/config, overridden by ?stt=server|browser for the tab.  Unknown
  // (older backend, maintenance, offline) means the browser engine.  After a
  // server-mode fallback the tab stays on the browser engine until reload.
  const [sttEngine, setSttEngine] = useState<SttEngine>("browser");
  const fellBackRef = useRef(false);
  useEffect(() => {
    const override = sttOverride(window.location.search, tabStorage());
    void fetchVoiceConfig().then((config) => {
      if (!fellBackRef.current) setSttEngine(resolveEngine(config, override));
    });
  }, []);

  const [micNotice, setMicNotice] = useState<string | null>(null);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showMicNotice = useCallback((text: string) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setMicNotice(text);
    noticeTimerRef.current = setTimeout(() => setMicNotice(null), NOTICE_MS);
  }, []);
  useEffect(() => () => { if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current); }, []);

  const {
    queue,
    stop: stopAudio,
    unlock: unlockAudio,
    play: playAudio,
    playbackState,
    autoplayBlocked,
    dismissAutoplayBlocked,
  } = useAudioPlayer(voiceBackend ? "kokoro" : "browser");

  // Which assistant message Scout's voice is reading, so only its Play button turns into Stop.
  const [audioOwnerId, setAudioOwnerId] = useState<string | null>(null);
  const speakingId = playbackState === "playing" && !autoplayBlocked ? audioOwnerId : null;

  // Buffer for sentence-boundary streaming TTS
  const tokenBufferRef = useRef("");

  // Stable refs
  const submitRef = useRef<((q: string, voiceInput?: boolean, alternatives?: string[], voiceEngine?: string) => Promise<void>) | null>(null);
  const resetVoiceRef = useRef<() => void>(() => {});

  // ---------------------------------------------------------------------------
  // Voice transcript handler
  // ---------------------------------------------------------------------------
  const handleTranscript = useCallback(
    (transcript: string, alternatives: string[], engine: string) => {
      // If iOS ended the recording itself (no stop tap), still hand the session back to playback.
      setAudioSession("playback");
      if (!transcript) return;
      setInputValue(transcript);
      void submitRef.current?.(transcript, true, alternatives, engine).then(() => setInputValue(""));
      resetVoiceRef.current();
    },
    [],
  );

  // A server-mode clip that could not be used: the browser engine takes over
  // for the rest of the tab (the budget or the provider is out), and the user
  // is asked to say it again since the recording cannot be reused.
  const onServerFallback = useCallback(() => {
    fellBackRef.current = true;
    setSttEngine("browser");
    showMicNotice(NOTICE_FALLBACK);
  }, [showMicNotice]);
  const onServerMiss = useCallback(() => showMicNotice(NOTICE_MISS), [showMicNotice]);

  const voiceMode = voiceBackend || sttEngine === "server" ? "server" : "browser";
  const { startRecording, stopRecording, resetState, voiceState, isSupported } =
    useVoiceInput(handleTranscript, voiceMode, { sessionId, onFallback: onServerFallback, onMiss: onServerMiss });

  // Nothing may play while the mic is on (owner's iPhone, 2026-10-26,
  // docs/voice-input-learnings.md §2.O): a recording after Scout had spoken
  // heard only silence.  The tap stops leftover speech and asks iOS for
  // play-and-record; the audio unlock waits for the stop tap.  The mic is
  // off while an answer streams.
  const startRecordingQuietly = useCallback(() => {
    if (isAsking) return;
    stopAudio();
    setAudioSession("play-and-record");
    void startRecording();
  }, [isAsking, stopAudio, startRecording]);

  // The stop tap is the user gesture that unlocks Scout's voice for the
  // answer, after the recogniser has let go of the mic.
  const stopRecordingWithUnlock = useCallback(() => {
    stopRecording();
    setAudioSession("playback");
    unlockAudio();
  }, [stopRecording, unlockAudio]);

  // Play (or replay) one message's answer through the same player as the
  // automatic narration, so its button can stop either.  Also the recovery
  // for blocked autoplay: the tap is the gesture iOS wanted.
  const playMessage = useCallback((id: string, text: string) => {
    dismissAutoplayBlocked();
    setAudioOwnerId(id);
    playAudio(text);
  }, [dismissAutoplayBlocked, playAudio]);

  useEffect(() => { resetVoiceRef.current = resetState; }, [resetState]);

  useEffect(() => {
    if (voiceState === "recording") setStatus("listening");
    else if (voiceState === "processing") setStatus("transcribing");
  }, [voiceState, setStatus]);

  // ---------------------------------------------------------------------------
  // Spacebar tap-to-toggle (desktop only)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isSupported) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat || isAsking) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON") return;
      e.preventDefault();
      if (voiceState === "recording" || voiceState === "starting") stopRecordingWithUnlock();
      else startRecordingQuietly();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isSupported, isAsking, voiceState, startRecordingQuietly, stopRecordingWithUnlock]);

  // ---------------------------------------------------------------------------
  // Submit question → SSE stream → sentence-by-sentence TTS as tokens arrive
  // ---------------------------------------------------------------------------
  const submitQuestion = useCallback(
    async (question: string, voiceInput = false, alternatives: string[] = [], voiceEngine = "browser") => {
      if (!sessionId || isAsking) return;

      // Stop any in-progress audio from a previous answer, THEN unlock the
      // iOS audio session.  Order matters: stop() calls speechSynthesis.cancel()
      // which would clear an unlock utterance if we fired it first.
      stopAudio();
      tokenBufferRef.current = "";

      // Unlock iOS speechSynthesis — fires a silent utterance within the
      // user-gesture context so async SSE callbacks can speak without
      // a separate "Play" tap.  Must come AFTER stopAudio().
      unlockAudio();

      // If any previous assistant message is still in "streaming" state
      // (e.g. the request was aborted or timed out), mark it done so it
      // shows "No answer found" rather than staying stuck on "Generating answer…".
      useChatStore.getState().messages
        .filter((m) => m.role === "assistant" && m.streaming)
        .forEach((m) => setStreamingDone(m.id));

      const userMsgId = crypto.randomUUID();
      addUserMessage(question, userMsgId);
      const msgId = crypto.randomUUID();
      addAssistantMessage(msgId);
      setAudioOwnerId(msgId);

      // Client-side fallback: if the message is still streaming after 50 s
      // (e.g. backend hung, AbortSignal.timeout didn't fire, network stall),
      // resolve it with an error so the bubble never stays stuck forever.
      const fallbackTimer = setTimeout(() => {
        const current = useChatStore.getState().messages.find((m) => m.id === msgId);
        if (current?.streaming) {
          tokenBufferRef.current = "";
          setError(msgId, "Scout took too long to respond. Please try again.");
        }
      }, 50_000);

      await ask({
        question,
        sessionId,
        messageId: msgId,
        callbacks: {
          // onToken — stream text AND queue TTS sentence-by-sentence
          onToken: (token, id) => {
            appendToken(token, id);
            tokenBufferRef.current += token;

            // Find the first sentence boundary in the buffer
            const match = SENTENCE_END.exec(tokenBufferRef.current);
            if (match) {
              const sentence = tokenBufferRef.current.slice(0, match.index + 1).trim();
              // Keep everything after the boundary (skip the whitespace)
              tokenBufferRef.current = tokenBufferRef.current
                .slice(match.index + match[0].length)
                .trimStart();
              if (sentence) queue(sentence);
            }
          },

          onVisual: (visual, id) => setVisual(visual, id),

          // onDone — flush any remaining buffer fragment
          onDone: (id) => {
            clearTimeout(fallbackTimer);
            setStreamingDone(id);
            const remaining = tokenBufferRef.current.trim();
            if (remaining) {
              queue(remaining);
              tokenBufferRef.current = "";
            }
          },

          onError: (err) => {
            clearTimeout(fallbackTimer);
            tokenBufferRef.current = "";
            setError(msgId, err || "Sorry, something went wrong. Please try again.");
          },

          onCorrection: voiceInput ? (corrected) => updateMessageContent(userMsgId, corrected) : undefined,
        },
        league: league ?? "all",
        voiceInput,
        alternatives,
        voiceEngine,
      });
    },
    [sessionId, isAsking, ask, league, unlockAudio, stopAudio, queue, addUserMessage, addAssistantMessage, appendToken, setVisual, setStreamingDone, setError, updateMessageContent],
  );

  useEffect(() => { submitRef.current = submitQuestion; }, [submitQuestion]);

  // Phase C — when a snapshot URL's "Ask Scout the same question now"
  // CTA brings the user back to / with ?ask=<question>, auto-submit
  // that question once the session is ready.  Guards against
  // re-submitting on every render via a one-shot ref keyed on the
  // question string.
  const askedFromUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId) return;
    const params = new URLSearchParams(window.location.search);
    const askParam = params.get("ask");
    if (!askParam || askedFromUrlRef.current === askParam) return;
    askedFromUrlRef.current = askParam;
    // Clear the param from the URL so a reload doesn't re-ask.
    window.history.replaceState(null, "", window.location.pathname);
    void submitRef.current?.(askParam, false);
  }, [sessionId]);

  // A card's tappable chip (a related glossary term, "more in the guide") asks
  // through a window event; submit it like a typed question.
  useEffect(() => onAskQuestion((q) => { void submitRef.current?.(q, false); }), []);

  const displayStatus: PipelineStatus = status;

  return (
    <>
      <Header league={league} onLeagueChange={setLeague} />
      <StatusIndicator />
      {isMaintenance && (
        <div
          role="status"
          aria-live="polite"
          className="mx-auto my-3 flex max-w-2xl items-start gap-3 rounded-lg border border-amber-500/60 bg-amber-500/10 px-4 py-3 text-sm text-amber-50"
        >
          <span aria-hidden className="text-lg leading-tight">⏸</span>
          <div className="flex-1">
            <p className="font-medium">Scout is taking a quick break to keep the lights on.</p>
            <p className="mt-1 text-amber-100/80">
              We'll be back soon — refresh in a few minutes, or tap{" "}
              <button
                type="button"
                onClick={() => setMaintenance(false)}
                className="underline underline-offset-2 hover:text-amber-50"
              >
                Try again
              </button>{" "}
              to retry.
            </p>
          </div>
        </div>
      )}
      {micNotice && (
        <p role="status" aria-live="polite" className="mx-auto max-w-2xl px-4 py-1 text-center text-xs text-amber-200/90">
          {micNotice}
        </p>
      )}
      <ChatThread
        messages={messages}
        onExampleClick={(q) => { void submitQuestion(q); }}
        autoplayBlocked={autoplayBlocked}
        speakingId={speakingId}
        onPlay={playMessage}
        onStop={stopAudio}
      />
      <InputBar
        value={inputValue}
        onChange={setInputValue}
        onSubmit={submitQuestion}
        micStart={startRecordingQuietly}
        micStop={stopRecordingWithUnlock}
        micRecording={voiceState === "recording"}
        micStarting={voiceState === "starting"}
        voiceEnabled={isSupported}
        disabled={isAsking || isMaintenance}
        micDisabled={isMaintenance || isAsking}
        status={displayStatus}
      />
      {/* First-visit only — persists `scoutdex_tour_v1_seen` to localStorage
          on completion or skip.  Finale step fires a sample question via
          this callback so the user sees Scout answer end-to-end. */}
      <Tour onSamplePrompt={(q) => { void submitQuestion(q); }} />
    </>
  );
}
